import { useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import { useForm } from '@/shared/lib/forms'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import api from '@/admin/lib/api'
import type { ApiKeyCreated, ApiKeyScope } from '@/admin/types/api-keys'
import type { FormContext, FormField } from '@/shared/lib/forms'

import { toastApiError } from '@/shared/lib/api-error'

type PartnerRef = { uuid: string; name: string } | null

type ApiKeyFormValues = {
  name: string
  description: string
  scope: ApiKeyScope
  partnership: PartnerRef
}

const isPartnerScope = (scope: ApiKeyScope) =>
  scope === 'partner' || scope === 'partner_read_only'

interface CreateApiKeyDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (data: ApiKeyCreated) => void
}

export function CreateApiKeyDialog({ open, onClose, onCreated }: CreateApiKeyDialogProps) {
  const [saving, setSaving] = useState(false)
  const lookupProviders = useResourceLookupProviders()

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'name',
        label: 'Name',
        placeholder: 'e.g., Production API Key',
        value: '',
        validationSchema: z.string().trim().min(1, 'Name is required'),
      },
      {
        type: 'text',
        name: 'description',
        label: 'Description',
        placeholder: 'Optional description',
        value: '',
        validationSchema: z.string().trim(),
      },
      {
        type: 'select',
        name: 'scope',
        label: 'Scope',
        value: 'full',
        options: [
          { label: 'Full access (whole organisation)', value: 'full' },
          { label: 'Single partner (act as partner)', value: 'partner' },
          { label: 'Single partner (read-only)', value: 'partner_read_only' },
        ],
      },
      {
        type: 'entity-search',
        name: 'partnership',
        label: 'Partner',
        placeholder: 'Search by name, member ID, or email',
        value: null,
        configurationData: { lookup_provider: 'partnership', icon: 'handshake' },
        visible: (ctx: FormContext) => isPartnerScope(ctx.values.scope as ApiKeyScope),
      },
    ],
    [],
  )

  const { Form, ref } = useForm(
    {
      name: 'create-api-key-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as ApiKeyFormValues
      if (isPartnerScope(values.scope) && !values.partnership?.uuid) {
        ref.current?.setFieldError('partnership', 'Select a partner')
        return
      }
      setSaving(true)
      try {
        const res = await api.post('/auth/api-keys/', {
          name: values.name.trim(),
          description: values.description.trim(),
          scope: values.scope,
          ...(isPartnerScope(values.scope)
            ? { partnership_uuid: values.partnership?.uuid }
            : {}),
        })
        const created = res.data as ApiKeyCreated
        toast.success('API key created')
        onCreated(created)
      } catch (err) {
        toastApiError(err, 'Failed to create API key')
      } finally {
        setSaving(false)
      }
    },
    [open, fields],
  )

  function handleClose() {
    ref.current?.resetForm()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) handleClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create API Key</DialogTitle>
        </DialogHeader>
        <ResourceLookupContextProvider providers={lookupProviders}>
          <Form className="space-y-4" />
        </ResourceLookupContextProvider>
        <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" form="create-api-key-form" disabled={saving}>
            {saving && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
